import { Link, useLocalSearchParams, type Href } from "expo-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, TextInput, useWindowDimensions, View, type TextStyle } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { Alert } from "@/src/lib/alert";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { useControlAccessContext } from "@/src/control/ControlAccessContext";
import { ControlDisclosure, ControlId, ControlKeyValue } from "@/src/control/ControlDetails";
import { decideIdentityRequest, getIdentityRequest, type IdentityRequestView } from "@/src/control/api";
import { hasControlPermission } from "@/src/control/permissions";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";
import {
  controlAccountTypeLabel,
  controlVerificationStatusLabel,
  formatControlDate,
  identityRequestKindLabel,
  identityRequestStatusLabel,
} from "@/src/control/presentation";

const breakableTextStyle: TextStyle | undefined = Platform.OS === "web"
  ? ({ overflowWrap: "anywhere", wordBreak: "break-word" } as unknown as TextStyle)
  : undefined;

export default function ControlIdentityDetailScreen() {
  return <ControlAccessGate permission="identity.requests.read"><IdentityDetail /></ControlAccessGate>;
}

function IdentityDetail() {
  const { width } = useWindowDimensions();
  const stackedColumns = width < 900;
  const params = useLocalSearchParams<{ requestId?: string | string[] }>();
  const requestId = useMemo(() => Array.isArray(params.requestId) ? params.requestId[0] : params.requestId || "", [params.requestId]);
  const [data, setData] = useState<IdentityRequestView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [decisionNote, setDecisionNote] = useState("");
  const access = useControlAccessContext();
  const canDecide = access.status === "allowed" && hasControlPermission(access.member, "identity.requests.decide") && !!data && data.request.status === "pending" && data.request.requesterUid !== access.member.uid;

  useEffect(() => {
    let active = true;
    if (!requestId) {
      setError(true);
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    setError(false);
    getIdentityRequest(requestId)
      .then((value) => active && setData(value))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, requestId]);

  function decide(decision: "approved" | "rejected") {
    if (!data || !canDecide || busy) return;
    if (decision === "rejected" && !decisionNote.trim()) {
      Alert.alert("Motif requis", "Ajoute un motif avant de refuser une demande.");
      return;
    }
    const label = decision === "approved" ? "Approuver" : "Refuser";
    Alert.alert("Confirmer la décision", `${label} cette demande ? La décision sera enregistrée dans l’audit.`, [
      { text: "Annuler", style: "cancel" },
      { text: label, style: decision === "rejected" ? "destructive" : "default", onPress: () => void executeDecision(decision) },
    ]);
  }

  async function executeDecision(decision: "approved" | "rejected") {
    if (!data) return;
    setBusy(true);
    try {
      await decideIdentityRequest(data.request.id, decision, decisionNote.trim() || undefined);
      setAttempt((value) => value + 1);
      setDecisionNote("");
    } catch (cause: unknown) {
      Alert.alert("Décision refusée", friendlyFirebaseError(cause, "La décision n’a pas pu être enregistrée."));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <State title="Chargement de la demande…"><ActivityIndicator color={text.brand} /></State>;
  if (error || !data) return <State title="Demande indisponible"><Pressable accessibilityRole="button" onPress={() => setAttempt((value) => value + 1)}><Text style={styles.link}>Réessayer</Text></Pressable></State>;

  const request = data.request;
  const requester = data.requester;
  const institutionRequest = request.kind === "institution_identity" || request.kind === "institution_conversion";
  const requesterName = requester?.displayName || (requester?.username ? `@${requester.username.replace(/^@/, "")}` : "Demandeur non renseigné");
  return (
    <ScrollView contentContainerStyle={styles.page}>
      <Link href={"/control/identity" as Href} asChild><Pressable accessibilityRole="link"><Text style={styles.back}>‹ Retour aux demandes</Text></Pressable></Link>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.kicker}>DEMANDE D’IDENTITÉ</Text>
          <Text style={styles.title}>{identityRequestKindLabel(request.kind)}</Text>
          <Text style={[styles.subtitle, breakableTextStyle]}>Demandée le {formatControlDate(request.createdAt)} · mise à jour le {formatControlDate(request.updatedAt)}</Text>
        </View>
        <StatusBadge status={request.status} />
      </View>

      <View style={[styles.columns, stackedColumns && styles.columnsStack]}>
        <View style={[styles.mainColumn, stackedColumns && styles.stackedColumn]}>
          <Section title="Demandeur">
            <Text style={[styles.name, breakableTextStyle]}>{requesterName}</Text>
            {requester?.username ? <Text style={[styles.handle, breakableTextStyle]}>@{requester.username.replace(/^@/, "")}</Text> : null}
            <View style={styles.keyValues}>
              <ControlKeyValue label="Type de compte" value={controlAccountTypeLabel(request.requesterAccountType || requester?.accountType)} />
              <ControlKeyValue label="Vérification actuelle" value={controlVerificationStatusLabel(requester?.verificationStatus)} />
              {requester?.mainSubject ? <ControlKeyValue label="Matière" value={requester.mainSubject} /> : null}
              {requester?.track ? <ControlKeyValue label="Parcours" value={requester.track} /> : null}
            </View>
            <ControlDisclosure>
              <ControlId label="UID du demandeur" value={request.requesterUid} />
            </ControlDisclosure>
          </Section>

          <Section title="Demande">
            <View style={styles.keyValues}>
              <ControlKeyValue label="Type de demande" value={identityRequestKindLabel(request.kind)} />
              <ControlKeyValue label="Compte concerné" value={controlAccountTypeLabel(request.requesterAccountType || requester?.accountType)} />
              {request.reviewData?.declaredInstitution ? <ControlKeyValue label="Institution déclarée" value={request.reviewData.declaredInstitution} /> : null}
            </View>
            <View style={styles.noteBlock}>
              <Text style={styles.fieldLabel}>Note du demandeur</Text>
              <Text style={[styles.body, breakableTextStyle]}>{request.note || "Aucune note libre."}</Text>
            </View>
            {request.requesterSnapshot ? (
              <View style={styles.snapshot}>
                <Text style={styles.fieldLabel}>Informations fournies au dépôt</Text>
                {request.requesterSnapshot.displayName ? <ControlKeyValue label="Nom" value={request.requesterSnapshot.displayName} /> : null}
                {request.requesterSnapshot.username ? <ControlKeyValue label="Handle" value={`@${request.requesterSnapshot.username.replace(/^@/, "")}`} /> : null}
                {request.requesterSnapshot.mainSubject ? <ControlKeyValue label="Matière" value={request.requesterSnapshot.mainSubject} /> : null}
                {request.requesterSnapshot.track ? <ControlKeyValue label="Parcours" value={request.requesterSnapshot.track} /> : null}
              </View>
            ) : null}
          </Section>

          <Section title="Suivi">
            <View style={styles.keyValues}>
              <ControlKeyValue label="Statut" value={identityRequestStatusLabel(request.status)} />
              <ControlKeyValue label="Créée" value={formatControlDate(request.createdAt)} />
              <ControlKeyValue label="Dernière mise à jour" value={formatControlDate(request.updatedAt)} />
            </View>
            {request.decisionNote ? <View style={styles.noteBlock}><Text style={styles.fieldLabel}>Motif de décision</Text><Text style={[styles.body, breakableTextStyle]}>{request.decisionNote}</Text></View> : null}
            <ControlDisclosure>
              <ControlId label="ID de la demande" value={request.id} />
              <ControlId label="UID du décideur" value={request.decidedBy || request.reviewedByUid} />
              <ControlId label="ID de l’identité institutionnelle" value={request.institutionIdentityId} />
            </ControlDisclosure>
          </Section>
        </View>

        <View style={[styles.sideColumn, stackedColumns && styles.stackedColumn]}>
          <Section title="Décision">
            {canDecide ? (
              <>
                <Text style={styles.note}>Vérifie les éléments fournis avant de confirmer. Un motif est obligatoire en cas de refus.</Text>
                <TextInput
                  accessibilityLabel="Motif de la décision"
                  editable={!busy}
                  multiline
                  onChangeText={setDecisionNote}
                  placeholder="Motif du refus (obligatoire pour un refus)"
                  placeholderTextColor={text.muted}
                  style={styles.noteInput}
                  value={decisionNote}
                />
                <DecisionButton busy={busy} label="Approuver la demande" onPress={() => decide("approved")} />
                <DecisionButton busy={busy} danger label="Refuser la demande" onPress={() => decide("rejected")} />
              </>
            ) : <Text style={styles.muted}>{request.status !== "pending" ? "Cette demande est déjà clôturée. Aucune nouvelle décision n’est disponible." : access.status === "allowed" && request.requesterUid === access.member.uid ? "Un membre ne peut pas décider sa propre demande." : "Tu peux consulter cette demande, mais tu n’as pas la permission de décider."}</Text>}
          </Section>
          <Section title="Après approbation">
            {institutionRequest ? <><Text style={styles.body}>Une approbation crée une identité institutionnelle canonique distincte.</Text><Text style={styles.muted}>Le serveur conserve le lien avec le demandeur et l’accès reste soumis aux permissions existantes.</Text></> : <Text style={styles.body}>Le serveur met à jour la vérification canonique du compte professeur et son historique.</Text>}
          </Section>
        </View>
      </View>
    </ScrollView>
  );
}

function DecisionButton({ label, onPress, busy, danger = false }: { label: string; onPress: () => void; busy: boolean; danger?: boolean }) {
  return <Pressable accessibilityRole="button" accessibilityState={{ busy, disabled: busy }} disabled={busy} onPress={onPress} style={[styles.decisionButton, !danger && styles.decisionButtonPrimary, danger && styles.decisionButtonDanger, busy && styles.disabled]}><Text style={danger ? styles.dangerText : styles.decisionText}>{busy ? "Traitement…" : label}</Text></Pressable>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <View style={styles.section}><Text style={styles.sectionTitle}>{title}</Text>{children}</View>;
}

function State({ title, children }: { title: string; children?: ReactNode }) {
  return <View style={styles.state}><Text style={styles.stateTitle}>{title}</Text>{children}</View>;
}

function StatusBadge({ status }: { status: IdentityRequestView["request"]["status"] }) {
  return <Text style={[styles.badge, status === "approved" ? styles.badgeSuccess : status === "rejected" ? styles.badgeDanger : status === "cancelled" ? styles.badgeMuted : styles.badgeActive]}>{identityRequestStatusLabel(status)}</Text>;
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  back: { color: text.brand, fontWeight: "800" },
  header: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.md, justifyContent: "space-between" },
  headerText: { flex: 1, flexShrink: 1, gap: spacing.sm, minWidth: 0 },
  kicker: { color: text.brand, fontSize: 11, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 27, fontWeight: "800" },
  subtitle: { color: text.secondary, lineHeight: 21, minWidth: 0 },
  columns: { flexDirection: "row", flexWrap: "wrap", gap: spacing.lg },
  columnsStack: { flexDirection: "column" },
  mainColumn: { flexBasis: 0, flexGrow: 2, flexShrink: 1, gap: spacing.lg, minWidth: 0 },
  sideColumn: { flexBasis: 0, flexGrow: 1, flexShrink: 1, gap: spacing.lg, minWidth: 0 },
  stackedColumn: { flexBasis: "auto", flexGrow: 0, width: "100%" },
  section: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  sectionTitle: { color: text.primary, fontSize: 17, fontWeight: "800", minWidth: 0 },
  name: { color: text.primary, fontSize: 18, fontWeight: "800", minWidth: 0 },
  handle: { color: text.secondary, fontSize: 13, minWidth: 0 },
  keyValues: { gap: spacing.md, minWidth: 0 },
  fieldLabel: { color: text.muted, fontSize: 11, fontWeight: "700" },
  noteBlock: { gap: spacing.xs, minWidth: 0 },
  snapshot: { backgroundColor: surface.secondary, borderRadius: radius.sm, gap: spacing.sm, padding: spacing.md },
  body: { color: text.primary, lineHeight: 22, minWidth: 0 },
  muted: { color: text.muted, lineHeight: 20, minWidth: 0 },
  note: { color: text.secondary, lineHeight: 21, minWidth: 0 },
  noteInput: { borderColor: border.strong, borderRadius: radius.sm, borderWidth: 1, color: text.primary, minHeight: 100, minWidth: 0, padding: spacing.md, textAlignVertical: "top", width: "100%" },
  decisionButton: { alignItems: "center", borderRadius: radius.sm, borderWidth: 1, minHeight: 48, justifyContent: "center", paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  decisionButtonPrimary: { backgroundColor: surface.brand, borderColor: surface.brand },
  decisionButtonDanger: { backgroundColor: surface.danger, borderColor: border.danger },
  decisionText: { color: text.inverse, fontWeight: "800", textAlign: "center" },
  dangerText: { color: text.danger, fontWeight: "800", textAlign: "center" },
  disabled: { opacity: 0.5 },
  badge: { borderRadius: radius.pill, fontSize: 12, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  badgeActive: { backgroundColor: surface.warning, color: text.brand },
  badgeSuccess: { backgroundColor: surface.success, color: text.info },
  badgeDanger: { backgroundColor: surface.danger, color: text.danger },
  badgeMuted: { backgroundColor: surface.selected, color: text.muted },
  state: { alignItems: "center", backgroundColor: surface.secondary, flex: 1, gap: spacing.md, justifyContent: "center", padding: spacing.xl },
  stateTitle: { color: text.primary, fontSize: 20, fontWeight: "800" },
  link: { color: text.brand, fontWeight: "800" },
});
